import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { requestWheels } from '../lib/utils';

export interface WheelsBrand {
    id: number;
    name: string;
    img: string;
}

/** `/sizes` renvoie la liste des cylindrées distinctes (nombres). */
export type WheelsSize = number;

export interface WheelsVehicle {
    sra: string;
    modele: string;
    annee_debut_version: string;
    annee_fin_version: string;
    prix_origine: number;
    version?: string;
    cylindree?: number;
    codecategorie?: string;
    genre?: string;
    groupe?: string;
    souscription_disabled?: boolean;
    type_vehicule?: string;
    brand?: { id: number; name: string };
    type?: { id: string };
}

export interface WheelsResponse<T> {
    data: T[];
}

export const useBrandsWheels = (type: number | string): UseQueryResult<WheelsResponse<WheelsBrand>> =>
    useQuery({
        queryKey: ['brands', type],
        queryFn: () => requestWheels.get<WheelsResponse<WheelsBrand>>(`/brands?type=${type}`).then((res) => res.data),
        enabled: Boolean(type),
    });

export const useSizesWheels = (type: number | string, brandId: number | undefined): UseQueryResult<WheelsResponse<WheelsSize>> =>
    useQuery({
        queryKey: ['sizes', type, brandId],
        queryFn: () => requestWheels.get<WheelsResponse<WheelsSize>>(`/sizes?type=${type}&brand_id=${brandId}`).then((res) => res.data),
        enabled: Boolean(type) && Boolean(brandId),
    });

export const useVehiclesWheels = (type: number | string, brandId: number | undefined, size: number | string): UseQueryResult<WheelsResponse<WheelsVehicle>> =>
    useQuery({
        queryKey: ['vehicles', type, brandId, size],
        queryFn: () => requestWheels.get<WheelsResponse<WheelsVehicle>>(`/vehicles?type=${type}&brand_id=${brandId}&size=${size}`).then((res) => res.data),
        enabled: Boolean(type) && Boolean(brandId) && Boolean(size),
    });

/** Détail d'un véhicule wheels (GET /vehicles/{sra}) : seuls les champs exploités côté formulaires sont typés. */
export interface WheelsVehicleDetail {
    sra: string;
    modele?: string;
    version?: string;
    cylindree?: number;
    codecategorie?: string;
    genre?: string;
    groupe?: string;
    classe?: string;
    type_vehicule?: string;
    souscription_disabled?: boolean;
    annee_debut_version?: string | number | null;
    annee_fin_version?: string | number | null;
    prix_origine?: number;
    brand?: { id: number; name: string };
    type?: { id: string };
}

export const fetchVehicleBySra = (sra: string): Promise<WheelsVehicleDetail> =>
    requestWheels.get<{ data: WheelsVehicleDetail }>(`/vehicles/${encodeURIComponent(sra)}`).then((res) => res.data.data);

/** Réponse `/vehicles/immatriculation/{immat}` (SIV/FVA). */
export interface WheelsImmatResponse {
    data?: {
        date_premiere_immat?: string;
        vehicles?: WheelsVehicle[];
        filtered?: boolean;
        has_same_stats?: boolean;
    };
    message?: string;
}

/** Recherche par immatriculation ; `a2Filter` restreint aux versions compatibles A2 (défaut wheels : true). */
export const useImmatWheels = (
    type: string | undefined,
    immatriculation: string | undefined,
    a2Filter = true,
    enabled = true,
): UseQueryResult<WheelsImmatResponse> =>
    useQuery({
        queryKey: ['immatriculation', type, immatriculation, a2Filter],
        queryFn: () =>
            requestWheels
                .get<WheelsImmatResponse>(
                    `/vehicles/immatriculation/${encodeURIComponent(immatriculation ?? '')}?type=${type}&a2_filter=${a2Filter ? 1 : 0}`,
                )
                .then((res) => res.data),
        enabled: enabled && Boolean(type) && Boolean(immatriculation),
    });
